import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { DeliveryCostPostBodyType, DeliveryCostPostResponseType } from "../server/deliveryCostEndpoint";
import type { DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import { setCarDeliveryConfig } from "../redux/actions/UscCarDeliveryActions";
import { calculateExternalPrice } from "../redux/actions/UscExternalPriceActions";
import { getAPI } from "../../../common-deprecated/settings/utils/commonSettingUtils";
import Debug from "../../../common-deprecated/Debug";
import type { UscDispatchType } from "../redux/store";

export enum CarDeliveryDestination {
    Home = "home",
    Dealer = "dealer",
}

export enum CarDeliveryTransportationOption {
    Truck = "truck",
    ProfessionalDriver = "professionalDriver",
}

// Used for the car delivery configurator modal.
export enum CarDeliveryNavStep {
    Destination = 1,
    Transportation = 2,
}

export type UscCarDeliveryPriceType = {
    cashPriceInclVat: number | null;
    cashPriceExclVat: number | null;
};

// Base car delivery config object.
// This contains all properties except for price(s), which are
// seperated in the final delivery config and the temp delivery config.
export type UscCarDeliveryBaseConfig = {
    destination: CarDeliveryDestination;
    dealerName: string;
    dealerId: string; // Only relevant if the destination is 'dealer'.
    address: { address: string; city: string; zip: string; geo: { lat: number; lon: number } };
    transportationType: CarDeliveryTransportationOption;
};

// Temporary car delivery config object.
// This is used to temporarily store the config between steps.
export type UscCarDeliveryTempConfig = Partial<UscCarDeliveryBaseConfig> & {
    prices?: Record<CarDeliveryTransportationOption, UscCarDeliveryPriceType>;
};

// Final car delivery config object.
// This contains all properties, including price(s).
// And is what is eventually used in checkout.
export type UscCarDeliveryConfig = UscCarDeliveryBaseConfig & {
    price: UscCarDeliveryPriceType;
};

// Used to save the delivery config in session storage
export type UscCarDeliveryConfigSessionType = {
    [dealerId: string]: UscCarDeliveryConfig | null;
};

/**
 * This function can be used to easily add a car delivery to the current config.
 * The data structure should be exactly the same as configuring it through the configurator modal.
 */
export const addMockDeliveryConfig = (
    dispatch: UscDispatchType,
    destination: CarDeliveryDestination,
    price: number,
    dealerId: string,
    transportationType: CarDeliveryTransportationOption = CarDeliveryTransportationOption.ProfessionalDriver,
): void => {
    const mockConfig: UscCarDeliveryConfig = {
        price: {
            cashPriceInclVat: price,
            cashPriceExclVat: price,
        },
        address: {
            address: "Av. du Bourget 60",
            city: "Bruxelles",
            zip: "1130",
            geo: {
                lat: 50.898,
                lon: 4.484,
            },
        },
        dealerName: "Toyota Brussels",
        destination,
        dealerId,
        transportationType,
    };

    dispatch(setCarDeliveryConfig(mockConfig));
    dispatch(calculateExternalPrice());
};

// -------------------------------
//  Delivery cost price calculation
//  This is currently done using Driiveme
//  https://www.driiveme.co.uk/developer/api.html?section=price-transport
//  We use the general name "delivery cost" in case we want to switch to another provider in the future.
// -------------------------------

export enum DeliveryCostMovement {
    OneWay = 1,
    CustomerDelivery = 2,
    ClientReturn = 3,
}

export enum DeliveryCostDriverType {
    ProfessionalDriver = 2,
    Truck = 3,
}

export enum DeliveryCostVehicleCategory {
    City = 1,
    Car = 2,
    Sports = 3,
    Mpv = 4,
    Suv = 5,
    Van = 6,
    Pickup = 7,
    FittedOutVan = 8,
}

/**
 * Creates a delivery cost POST body based on the current car delivery config.
 */
const createDeliveryCostBody = (
    startingPoint: string,
    config: UscCarDeliveryConfig,
    transportationType: DeliveryCostDriverType,
): DeliveryCostPostBodyType => ({
    type:
        config.destination === CarDeliveryDestination.Dealer
            ? DeliveryCostMovement.CustomerDelivery
            : DeliveryCostMovement.OneWay,
    driverType: transportationType,
    departure: {
        fullAddress: startingPoint,
    },
    destination: {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        fullAddress: `${config.address?.address} ${config.address?.zip} ${config.address?.city}`,
    },
    vehicle: {
        category: DeliveryCostVehicleCategory.Car,
        transportType: DeliveryCostDriverType.ProfessionalDriver,
    },
});

const fetchDeliveryCostPrice = async (
    body: DeliveryCostPostBodyType,
    url: string,
): Promise<DeliveryCostPostResponseType | { error: boolean }> => {
    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            return { error: true };
        }

        return response.json();
    } catch (exception) {
        Debug.error("Failed to fetch delivery cost price", exception);
        return { error: true };
    }
};

type DeliveryCostPricesResponseType = Record<
    CarDeliveryTransportationOption,
    {
        cashPriceInclVat: number | null;
        cashPriceExclVat: number | null;
    }
>;

/**
 * Fetches the delivery cost price for both transportation options.
 * Returns an object with the prices.
 * If a price could not be fetched, the value will be null. -> This should probably be changed to return/throw an error instead.
 **/
export const fetchDeliveryCostPrices = async (
    currentDealer: DealerResultType,
    deliveryConfig: UscCarDeliveryConfig,
    commonSettings: CommonSettingsType,
): Promise<DeliveryCostPricesResponseType | { error: boolean }> => {
    const selectedDealerAddress = `${currentDealer.address.line1} ${currentDealer.address.zip} ${currentDealer.address.city}`;
    const url = `${getAPI(commonSettings, "delivery-cost")}`;

    try {
        // We need to create 2 requests, one for the truck prices and one for the professional driver prices.
        const deliveryCostResponses = await Promise.all([
            fetchDeliveryCostPrice(
                createDeliveryCostBody(selectedDealerAddress, deliveryConfig, DeliveryCostDriverType.Truck),
                url,
            ),
            fetchDeliveryCostPrice(
                createDeliveryCostBody(
                    selectedDealerAddress,
                    deliveryConfig,
                    DeliveryCostDriverType.ProfessionalDriver,
                ),
                url,
            ),
        ]);

        const [truckResponse, professionalDriverResponse] = deliveryCostResponses;

        // If one of the requests failed, return an error.
        if ("error" in truckResponse || "error" in professionalDriverResponse) {
            return { error: true };
        } else {
            return {
                truck: {
                    cashPriceInclVat: truckResponse?.priceTtc || null,
                    cashPriceExclVat: truckResponse?.price || null,
                },
                professionalDriver: {
                    cashPriceInclVat: professionalDriverResponse?.priceTtc || null,
                    cashPriceExclVat: professionalDriverResponse?.price || null,
                },
            };
        }
    } catch (exception) {
        Debug.error("Failed to fetch delivery cost prices", exception);
        return { error: true };
    }
};

/**
 * Since we receive possible empty city/zip/address values from the Mapbox API,
 * we need to filter out empty values and concatenate the remaining ones.
 * This is used to create a full address string used in the frontend.
 */
export const createFullAddress = (address: UscCarDeliveryConfig["address"]): string => {
    // The address object also contains a geo object, which we don't want to include in the address string.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { geo, ...addressWithoutGeo } = address;

    return Object.values(addressWithoutGeo)
        .filter((value) => value !== null && value !== "")
        .join(" ");
};
